import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { Trophy, Medal, Calendar, CreditCard, Users, ArrowRight, Activity, Vote, PartyPopper, Sparkles, Clock } from "lucide-react";
import { trophiesApi, eventsApi, financeApi, membersApi, sportsApi, socialApi, errMsg } from "@/lib/api";
import { PageContainer, SectionTitle, Logo, UserAvatar, Money, SportChip, EmptyState } from "@/components/shared";
import { Reveal, StaggerGroup, StaggerItem, AnimatedCounter, ProgressBar, ConfettiBurst, shouldCelebrate, SkeletonGrid, SkeletonList } from "@/components/motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";

const fade = { initial: { opacity: 0, y: 14 }, animate: { opacity: 1, y: 0 } };

/* Festive "pop" entrance for the Stars of the Month cards */
const festivePop = {
  hidden: { opacity: 0, y: 26, scale: 0.9, rotate: -1.2 },
  show: { opacity: 1, y: 0, scale: 1, rotate: 0, transition: { type: "spring", stiffness: 240, damping: 17 } },
};

const MonthlyStarCard = ({ m, index, onProfile, t }) => {
  const reduce = useReducedMotion();
  const label = m.type === "athlete" ? t("home.athleteMonth") : t("home.coachMonth");
  return (
    <motion.div variants={reduce ? undefined : festivePop} className="relative">
      <Card
        className="group relative overflow-hidden ring-1 ring-trophy-gold/25 transition-shadow duration-200 hover:-translate-y-[2px] hover:ring-trophy-gold/60 hover:shadow-[0_16px_44px_-16px_hsl(var(--trophy-gold)/0.75)]"
        data-testid="accueil-vedette-mois-carte"
      >
        <div className="relative overflow-hidden asl-hero-gradient asl-noise p-4 text-white">
          {/* Soft golden aura */}
          <div className="pointer-events-none absolute -right-8 -top-10 h-28 w-28 rounded-full bg-trophy-gold/25 blur-2xl" aria-hidden="true" />
          {/* One-shot diagonal shine sweep on appearance */}
          {!reduce && (
            <motion.div
              aria-hidden="true"
              className="pointer-events-none absolute inset-y-0 -inset-x-2 z-10"
              style={{ background: "linear-gradient(105deg, transparent 42%, rgba(255,255,255,0.45) 50%, transparent 58%)" }}
              initial={{ x: "-160%" }}
              whileInView={{ x: "160%" }}
              viewport={{ once: true }}
              transition={{ duration: 1.05, ease: "easeInOut", delay: 0.35 + index * 0.15 }}
            />
          )}
          {/* Twinkling sparkle accents */}
          <Sparkles className="asl-twinkle absolute right-9 top-2.5 h-3.5 w-3.5 text-trophy-gold" style={{ animationDelay: "0.2s" }} aria-hidden="true" />
          <Sparkles className="asl-twinkle absolute right-3 top-9 h-3 w-3 text-white/70" style={{ animationDelay: "1s" }} aria-hidden="true" />
          <div className="absolute right-3 top-3"><Trophy className="h-5 w-5 text-trophy-gold" /></div>
          <div className="relative flex items-center gap-3">
            <UserAvatar user={m.recipient} size="h-14 w-14" className="ring-2 ring-trophy-gold/50" />
            <div className="min-w-0">
              <Badge className="mb-1 border-0 bg-trophy-gold/20 text-trophy-gold">{label}</Badge>
              <div className="truncate font-heading font-bold leading-tight">{m.recipient?.first_name} {m.recipient?.last_name}</div>
              <div className="text-xs text-white/70">{m.sport_name}</div>
            </div>
          </div>
        </div>
        <CardContent className="flex items-center justify-between gap-2 p-3">
          <span className="inline-flex items-center gap-1 text-sm"><Trophy className="h-4 w-4 text-trophy-gold" />{m.recipient?.total_trophies || 0} {t("home.trophies")}</span>
          <Button size="sm" variant="ghost" onClick={() => onProfile(m.recipient?.id)} data-testid="accueil-vedette-mois-profil">{t("home.profile")}</Button>
        </CardContent>
      </Card>
    </motion.div>
  );
};

function Countdown({ iso }) {
  const [txt, setTxt] = useState("");
  useEffect(() => {
    const tick = () => {
      const diff = new Date(iso) - new Date();
      if (diff <= 0) { setTxt("En cours"); return; }
      const d = Math.floor(diff / 86400000), h = Math.floor((diff % 86400000) / 3600000), m = Math.floor((diff % 3600000) / 60000);
      setTxt(d > 0 ? `${d}j ${h}h` : `${h}h ${m}m`);
    };
    tick(); const t = setInterval(tick, 60000); return () => clearInterval(t);
  }, [iso]);
  return <span>{txt}</span>;
}

const VedetteCard = ({ sport, onFeliciter }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const winnerName = (t) => t?.recipient ? `${t.recipient.first_name} ${t.recipient.last_name}` : null;
  return (
    <div data-testid="vedette-sport-carte" className="group relative min-w-[86%] snap-start overflow-hidden rounded-2xl asl-hero-gradient asl-noise p-5 text-white shadow-lg sm:min-w-[360px] md:min-w-0">
      <div className="pointer-events-none absolute -right-6 -top-6 h-28 w-28 rounded-full bg-trophy-gold/20 blur-2xl" />
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold"><Activity className="h-3.5 w-3.5 text-secondary" /> {sport.sport_name}</span>
        <Trophy className="h-5 w-5 text-trophy-gold" />
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3">
        {[{ key: "athlete", label: "Athlète", icon: Trophy }, { key: "coach", label: "Entraîneur", icon: Medal }].map(({ key, label, icon: Icon }) => {
          const t = sport[key];
          return (
            <div key={key} className={`relative rounded-xl p-3 ${t?.recipient ? "bg-white/10 glow-gold" : "bg-white/5"}`}>
              <div className="mb-2 flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-white/60"><Icon className="h-3.5 w-3.5 text-trophy-gold" /> {label} sem.</div>
              {t?.recipient ? (
                <>
                  <button onClick={() => navigate(`/profil/${t.recipient.id}`)} className="flex w-full items-center gap-2 text-left" data-testid="vedette-profil-lien">
                    <UserAvatar user={t.recipient} size="h-10 w-10" className="ring-2 ring-trophy-gold/50" />
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold">{winnerName(t)}</div>
                      <div className="inline-flex items-center gap-1 text-[11px] text-trophy-gold"><Trophy className="h-3 w-3" /> {t.recipient.total_trophies || 0}</div>
                    </div>
                  </button>
                  {t.reason ? <div className="mt-1.5 line-clamp-2 text-[11px] text-white/60">« {t.reason} »</div> : null}
                </>
              ) : <div className="text-xs text-white/50">Non attribué</div>}
            </div>
          );
        })}
      </div>
      <div className="mt-3 flex items-center gap-2">
        <Button size="sm" variant="outline" className="h-8 flex-1 border-white/30 bg-white/5 text-white hover:bg-white/15" onClick={() => navigate(`/sports/${sport.sport_id}`)} data-testid="vedette-section-bouton">Voir la section</Button>
        <Button size="sm" className="h-8 flex-1 bg-secondary hover:bg-emerald-600 text-white" onClick={() => onFeliciter(sport)} data-testid="vedette-feliciter-bouton"><PartyPopper className="mr-1 h-3.5 w-3.5" />Féliciter</Button>
      </div>
    </div>
  );
};

export default function HomePage() {
  const { user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [vedettes, setVedettes] = useState([]);
  const [events, setEvents] = useState([]);
  const [collectes, setCollectes] = useState([]);
  const [votes, setVotes] = useState([]);
  const [sports, setSports] = useState([]);
  const [directory, setDirectory] = useState([]);
  const [monthly, setMonthly] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [v, e, c, vt, s, d, mo] = await Promise.all([
          trophiesApi.weeklyCurrent(), eventsApi.list({ upcoming: true }), financeApi.collectes(true),
          trophiesApi.votes(), sportsApi.list(), membersApi.directory(), trophiesApi.monthlyCurrent(),
        ]);
        setVedettes(v.data); setEvents(e.data.slice(0, 5)); setCollectes(c.data);
        setVotes(vt.data); setSports(s.data); setDirectory(d.data); setMonthly(mo.data.items || []);
      } finally { setLoading(false); }
    })();
  }, []);

  const trophyIds = useMemo(() => vedettes.flatMap((s) => [s.athlete?.id, s.coach?.id]).filter(Boolean), [vedettes]);
  const celebrate = useMemo(() => (trophyIds.length ? shouldCelebrate("asl_seen_trophies", trophyIds) : false), [trophyIds]);

  const monthlyIds = useMemo(() => monthly.slice(0, 3).map((m) => m.id).filter(Boolean), [monthly]);
  const celebrateMonthly = useMemo(() => (monthlyIds.length ? shouldCelebrate("asl_seen_monthly_stars", monthlyIds) : false), [monthlyIds]);

  const nextEvent = events[0];
  const activeCollecte = collectes[0];

  const feliciter = async (sport) => {
    if (!user) { navigate("/connexion"); return; }
    const names = [sport.athlete, sport.coach].filter((t) => t?.recipient).map((t) => `${t.recipient.first_name} ${t.recipient.last_name}`);
    if (!names.length) return;
    try {
      await socialApi.createPost({ content: `Félicitations à ${names.join(" et ")}, vedette(s) de la semaine en ${sport.sport_name} ! 👏🏆`, sport_id: sport.sport_id });
      toast.success("Félicitations publiées dans le fil !");
    } catch (e) { toast.error(errMsg(e, "Réservé aux membres actifs")); }
  };

  const stats = [
    { label: t("home.stat.members"), value: directory.length, icon: Users },
    { label: t("home.stat.disciplines"), value: sports.length, icon: Activity },
    { label: t("home.stat.nextEvent"), custom: nextEvent ? <Countdown iso={nextEvent.start} /> : "—", icon: Calendar },
    { label: t("home.stat.collecte"), value: activeCollecte?.amount, money: true, icon: CreditCard },
  ];

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden asl-hero-gradient asl-noise">
        <div className="pointer-events-none absolute inset-0">
          <div className="asl-blob absolute -left-10 top-10 h-48 w-48 rounded-full bg-secondary/25 blur-3xl" />
          <div className="asl-blob-2 absolute right-0 top-24 h-56 w-56 rounded-full bg-trophy-gold/15 blur-3xl" />
          <div className="asl-blob absolute bottom-0 left-1/3 h-40 w-40 rounded-full bg-white/10 blur-3xl" />
        </div>
        <div className="relative mx-auto max-w-6xl px-4 sm:px-6 py-12 sm:py-16">
          <motion.div {...fade} transition={{ duration: 0.45 }} className="max-w-2xl text-white">
            <div className="mb-5"><Logo size={56} light /></div>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight">
              {t("home.title")} <span className="text-secondary">{t("home.brand")}</span>
            </h1>
            <p className="mt-4 text-base sm:text-lg text-white/70">{t("home.subtitle")}</p>
            <div className="mt-7 flex flex-wrap gap-3">
              {!user && <Button size="lg" className="bg-secondary hover:bg-emerald-600 text-white shadow-lg shadow-secondary/30" onClick={() => navigate("/inscription")} data-testid="hero-inscription-bouton">{t("home.becomeMember")} <ArrowRight className="ml-1.5 h-4 w-4" /></Button>}
              <Button size="lg" variant="outline" className="border-white/30 bg-white/5 text-white hover:bg-white/15" onClick={() => navigate("/sports")} data-testid="hero-sports-bouton">{t("home.discoverSports")}</Button>
            </div>
          </motion.div>

          {/* Animated stats */}
          <motion.div initial="hidden" animate="show" variants={{ hidden: {}, show: { transition: { staggerChildren: 0.08, delayChildren: 0.2 } } }}
            className="mt-9 grid max-w-3xl grid-cols-2 gap-3 sm:grid-cols-4">
            {stats.map((s, i) => (
              <motion.div key={i} variants={{ hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0 } }}
                className="rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur">
                <s.icon className="mb-2 h-5 w-5 text-secondary" />
                <div className="text-2xl font-heading font-bold text-white">
                  {s.custom ? s.custom : s.money ? <>{s.value != null ? <AnimatedCounter value={s.value} decimals={0} suffix=" $" /> : "—"}</> : <AnimatedCounter value={s.value || 0} />}
                </div>
                <div className="text-[11px] text-white/60">{s.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <PageContainer>
        {loading ? (
          <SkeletonGrid count={3} />
        ) : (
          <>
            <ConfettiBurst fire={celebrate} />

            {votes.length > 0 && (
              <Reveal>
                {votes.map((vt) => (
                  <Card key={vt.id} className="border-secondary/40 bg-secondary/5" data-testid="home-vote-banniere">
                    <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
                      <div className="flex items-center gap-3">
                        <div className="rounded-full bg-secondary/15 p-2"><Vote className="h-5 w-5 text-secondary" /></div>
                        <div>
                          <div className="font-heading font-semibold">Vote ouvert — {vt.type === "athlete" ? "Athlète" : "Entraîneur"} du mois ({vt.sport_name})</div>
                          <div className="text-sm text-muted-foreground">Égalité à départager. Réservé aux membres actifs — 48h.</div>
                        </div>
                      </div>
                      <Button size="sm" onClick={() => navigate("/tableau-de-bord")} data-testid="home-voter-bouton">Voter</Button>
                    </CardContent>
                  </Card>
                ))}
              </Reveal>
            )}

            {/* Vedettes du mois */}
            {monthly.length > 0 && (
              <section className="relative space-y-4" data-testid="accueil-vedettes-mois">
                <ConfettiBurst fire={celebrateMonthly} pieces={34} />
                <SectionTitle action={<Link to="/social" className="inline-flex items-center gap-1 text-sm text-secondary hover:underline">{t("home.link.network")} <ArrowRight className="h-3.5 w-3.5" /></Link>}>
                  <span className="inline-flex items-center gap-2"><Trophy className="h-5 w-5 text-trophy-gold" /> {t("home.section.starsMonth")}</span>
                </SectionTitle>
                <StaggerGroup className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" stagger={0.12}>
                  {monthly.slice(0, 3).map((m, i) => (
                    <MonthlyStarCard key={m.id} m={m} index={i} onProfile={(id) => navigate(`/profil/${id}`)} t={t} />
                  ))}
                </StaggerGroup>
              </section>
            )}

            {/* Vedettes */}
            <section className="space-y-4" data-testid="accueil-vedettes-semaine">
              <SectionTitle action={<Link to="/tableau-de-bord" className="inline-flex items-center gap-1 text-sm text-secondary hover:underline">{t("home.link.palmares")} <ArrowRight className="h-3.5 w-3.5" /></Link>}>
                <span className="inline-flex items-center gap-2"><Sparkles className="h-5 w-5 text-trophy-gold" /> {t("home.section.starsWeek")}</span>
              </SectionTitle>
              {vedettes.length === 0 ? (
                <EmptyState title={t("home.noSport")} description={t("home.noSportDesc")} icon={Trophy} />
              ) : (
                <div className="flex snap-x-mandatory gap-4 overflow-x-auto pb-2 no-scrollbar md:grid md:grid-cols-2 md:overflow-visible lg:grid-cols-3">
                  {vedettes.map((s) => <VedetteCard key={s.sport_id} sport={s} onFeliciter={feliciter} />)}
                </div>
              )}
            </section>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              {/* Events */}
              <section className="space-y-3 lg:col-span-2">
                <SectionTitle action={<Link to="/calendrier" className="text-sm text-secondary hover:underline">{t("home.link.calendar")}</Link>}>{t("home.section.events")}</SectionTitle>
                {events.length === 0 ? <EmptyState title={t("home.noEvents")} icon={Calendar} /> : (
                  <StaggerGroup className="space-y-3">
                    {events.map((e) => (
                      <StaggerItem key={e.id}>
                        <Card className="card-hover cursor-pointer" onClick={() => navigate("/calendrier")}>
                          <CardContent className="flex items-center gap-4 p-4">
                            <div className="flex h-14 w-14 shrink-0 flex-col items-center justify-center rounded-xl bg-primary text-primary-foreground">
                              <span className="text-[10px] uppercase">{new Date(e.start).toLocaleDateString("fr-CA", { month: "short" })}</span>
                              <span className="text-xl font-bold leading-none">{new Date(e.start).getDate()}</span>
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2"><span className="truncate font-semibold">{e.title}</span><SportChip name={e.sport_name} color={e.sport_color} /></div>
                              <div className="text-sm text-muted-foreground">{new Date(e.start).toLocaleString("fr-CA", { hour: "2-digit", minute: "2-digit" })} · {e.location || "À confirmer"}</div>
                            </div>
                            <Badge variant="outline" className="hidden gap-1 sm:inline-flex"><Clock className="h-3 w-3" /><Countdown iso={e.start} /></Badge>
                          </CardContent>
                        </Card>
                      </StaggerItem>
                    ))}
                  </StaggerGroup>
                )}
              </section>

              {/* Collecte progress */}
              <section className="space-y-3">
                <SectionTitle action={<Link to="/cotisations" className="text-sm text-secondary hover:underline">{t("home.link.seeAll")}</Link>}>{t("home.section.collecte")}</SectionTitle>
                {!activeCollecte ? <EmptyState title={t("home.noCollecte")} icon={CreditCard} /> : (
                  <Reveal>
                    <Card className="card-hover">
                      <CardContent className="p-5">
                        <div className="flex items-start justify-between gap-2"><div className="font-heading font-semibold leading-tight">{activeCollecte.title}</div><Badge variant="outline">{activeCollecte.sport_name}</Badge></div>
                        {activeCollecte.goal ? (
                          <div className="mt-4 space-y-2">
                            <div className="flex items-end justify-between text-sm"><span className="text-muted-foreground">{t("home.objective")}</span><Money value={activeCollecte.goal} /></div>
                            <ProgressBar value={activeCollecte.total_collected} max={activeCollecte.goal} />
                            <div className="flex justify-between text-xs text-muted-foreground"><span><Money value={activeCollecte.total_collected} className="text-secondary" /> {t("home.collected")}</span><span>{activeCollecte.paid_count} {t("home.membersCount")}</span></div>
                          </div>
                        ) : <div className="mt-3"><Money value={activeCollecte.amount} className="text-2xl text-secondary" /></div>}
                        <Button size="sm" className="mt-4 w-full bg-secondary hover:bg-emerald-600 text-white" onClick={() => navigate("/cotisations")} data-testid="home-cotiser-bouton">{t("home.cotiser")}</Button>
                      </CardContent>
                    </Card>
                  </Reveal>
                )}
              </section>
            </div>

            {/* CTA */}
            <Reveal>
              <Card className="relative overflow-hidden asl-hero-gradient asl-noise border-0 text-white">
                <CardContent className="flex flex-wrap items-center justify-between gap-4 p-6">
                  <div className="flex items-center gap-3">
                    <div className="rounded-full bg-white/10 p-3"><Users className="h-6 w-6 text-secondary" /></div>
                    <div><div className="font-heading text-lg font-semibold">{t("home.directory.title")}</div><div className="text-sm text-white/70">{t("home.directory.desc")}</div></div>
                  </div>
                  <Button variant="outline" className="border-white/30 bg-white/5 text-white hover:bg-white/15" onClick={() => navigate("/repertoire")} data-testid="home-repertoire-bouton">{t("home.directory.cta")}</Button>
                </CardContent>
              </Card>
            </Reveal>
          </>
        )}
      </PageContainer>
    </div>
  );
}
